import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { Button } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import type { FC, ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';

import { useAppSelector } from 'shared/lib';
import { sendEventShowStep } from 'shared/lib/sendGAEvents';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import { useSyncFormFieldsToQueryParams } from 'shared/lib/useSyncFormFieldsToQueryParams/useSyncFormFieldsToQueryParams';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import type { TCreditPurposeFormFields } from 'entities/creditForm';
import { isAutoValidationInProgressSelector } from 'entities/creditForm';
import { useDevFillMockDataTool } from 'entities/creditForm/lib/useDevFillMockDataTool';
import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';

import { getFormFieldsNavigationSequence } from '../../CreditPurposeInfo.config';
import { CreditPurposeInfoTexts } from '../../CreditPurposeInfo.texts';
import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { useCarInfoDictionariesLoader } from '../../lib/useCarInfoDictionariesLoader';
import { useIsShowAutoFields } from '../../lib/useIsShowAutoFields';
import { selectCreditPurposeFormViewOptions } from '../../model/creditPurposeFormView.selectors';
import type { TMobileAppBlocks } from '../../types/mobileAppBlocks';

type TCreditPurposeInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
  productCardBlock?: ReactNode;
  productTabTitle?: string;
  mobileApp?: TMobileAppBlocks;
};

type Props = FormComponentProps<TCreditPurposeFormFields, TCreditPurposeInfoFormView>;

const CreditPurposeInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const {
    setHeader,
    isFormForceOpened,
    formId,
    isAnyFormStepOpenedInPopup,
    analyticsOverrides,
    productCardBlock,
    productTabTitle,
    mobileApp,
  } = additionalProps || {};
  const viewOptions = useAppSelector(selectCreditPurposeFormViewOptions);
  const { watch } = useFormContext<TCreditPurposeFormFields>();
  const isMobile = useIsMobile();
  const isAutoValidationInProgress = useAppSelector(isAutoValidationInProgressSelector);

  const productTabTitleForHeader = productCardBlock ? productTabTitle : undefined;
  const [title, subtitle] = useAccordionHeaderParams(watch(), {}, false, {
    isCreditCardsForm: viewOptions.isCreditCardForm,
    productTabTitle: productTabTitleForHeader,
  });

  const navigationSequence = useMemo(
    () =>
      getFormFieldsNavigationSequence({
        isWithoutPeriod: viewOptions.isWithoutPeriod,
        isWithoutPurposeField: viewOptions.isWithoutPurposeField,
      }),
    [viewOptions.isWithoutPeriod, viewOptions.isWithoutPurposeField],
  );

  useEffect(() => {
    if (!isAutoValidationInProgress) {
      sendEventShowStep(1, 'Цель кредита', analyticsOverrides);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSyncFormFieldsToQueryParams({
    amount: watch('amount'),
    period: watch('period.value'),
    purpose: watch('purpose.value'),
  });

  useCarInfoDictionariesLoader();

  useSendFieldErrorAnalyticsEvent<TCreditPurposeFormFields>(1, CreditPurposeInfoTexts, analyticsOverrides);
  useDevFillMockDataTool<TCreditPurposeFormFields>({
    type: 'creditPurpose',
    stepLabel: 'Цель кредита',
  });

  const { controls } = useCommonFormBodyHooks({
    isFormForceOpened,
    currentControls: navigationSequence,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  const { isShowAutoYearField, isShowAutoModelField, isShowAutoFields } = useIsShowAutoFields();
  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;
  const isShowInlineSubmitButton = viewOptions.isWithoutPeriod && !isStickyButton && !isDialog;
  const isShowPurposeField = !viewOptions.isWithoutPurposeField;

  return (
    <>
      <FormWithControlledNavigation
        formId={formId}
        onSubmit={onSubmit}
        controlElement={FieldConstructor}
        isDialog={isDialog}
        isLoading={isLoading}
        submitButtonText={submitButtonText}
        navigationControls={controls}
        withSubmitButtons={!isShowInlineSubmitButton && !isStickyButton}
      >
        <Form.Block>
          {productCardBlock && <Form.Row colWidths={[12]}>{productCardBlock}</Form.Row>}
          <Form.Row>
            {isShowPurposeField && <FieldConstructor type={NAMES.purpose} />}

            <FieldConstructor type={NAMES.amount} />

            {!viewOptions.isWithoutPeriod && <FieldConstructor type={NAMES.period} />}
            {isShowInlineSubmitButton && (
              <Button
                type="submit"
                size={60}
                variant="primary"
              >
                {submitButtonText}
              </Button>
            )}
          </Form.Row>
          {isShowAutoFields && (
            <>
              <Form.Row>
                <FieldConstructor type={NAMES.initialFee} />

                <FieldConstructor type={NAMES.autoBrand} />

                {isShowAutoModelField && <FieldConstructor type={NAMES.autoModel} />}
              </Form.Row>
              {isShowAutoYearField && (
                <Form.Row>
                  <FieldConstructor type={NAMES.autoYear} />
                </Form.Row>
              )}
            </>
          )}
        </Form.Block>
        <FormStickyButton
          onClick={onSubmit}
          enabled={isStickyButton}
          isLoading={isLoading}
        >
          {submitButtonText}
        </FormStickyButton>

        {isMobile ? mobileApp?.mobile : null}
      </FormWithControlledNavigation>

      {isMobile ? null : mobileApp?.desktop}
    </>
  );
};

export const CreditPurposeInfoViewHoc = formProviderHOC<TCreditPurposeFormFields, TCreditPurposeInfoFormView>(
  CreditPurposeInfoFormView,
);
